import { API_URL } from "env";
import { mixLogOut } from "../services/mixin";

/**
 * API thông tin cá nhân của nhân sự đang đăng nhập (khu vực backend /be).
 * - getProfile():    GET  /api/v1/be/profile        -> lấy thông tin hiện tại
 * - updateProfile(): POST /api/v1/be/update-profile -> cập nhật tên/sđt/email/mật khẩu
 */

export interface ProfileInfo {
    id?: number;
    fullname?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    avatar?: string;
    address?: string;
    [key: string]: any;
}

export interface ProfileResponse {
    status: boolean;
    msg?: string;
    data: ProfileInfo | null;
}

export async function getProfile(): Promise<ProfileResponse> {
    try {
        const token = localStorage.getItem("token") || "";
        const response = await fetch(`${API_URL}/api/v1/be/profile`, {
            method: "GET",
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();
        // /be/profile trả: data = { fullname, email, type, info: { name, phone, avatar, ... } }
        // Gộp data + data.info để lấy đủ phone/avatar (nằm trong info).
        const d = json?.data ?? json ?? null;
        const merged =
            d && typeof d === "object" ? { ...d, ...(d.info || {}) } : d;
        return { status: true, data: merged };
    } catch (error) {
        console.error("Failed to fetch profile:", error);
        return { status: false, data: null };
    }
}

export interface UpdateProfileParams {
    name?: string;
    phone?: string;
    email?: string;
    // Ảnh đại diện mới (định dạng image, tối đa 5MB)
    avatar?: File;
    // Chỉ truyền khi nhân sự muốn đổi mật khẩu
    current_pass?: string;
    new_pass?: string;
    confirm_pass?: string;
}

export async function updateProfile(params: UpdateProfileParams): Promise<ProfileResponse> {
    try {
        const token = localStorage.getItem("token") || "";

        // Dùng FormData để gửi kèm file avatar (multipart/form-data).
        // Không tự set Content-Type để trình duyệt tự thêm boundary.
        const body = new FormData();
        if (params.name !== undefined) body.append("name", params.name);
        if (params.phone !== undefined) body.append("phone", params.phone);
        if (params.email !== undefined) body.append("email", params.email);
        if (params.avatar) body.append("avatar", params.avatar);
        if (params.current_pass) body.append("current_pass", params.current_pass);
        if (params.new_pass) body.append("new_pass", params.new_pass);
        if (params.confirm_pass) body.append("confirm_pass", params.confirm_pass);

        const response = await fetch(`${API_URL}/api/v1/be/update-profile`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
            },
            body,
        });

        const json = await response.json().catch(() => ({}));

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            return { status: false, msg: json?.msg || `HTTP error! status: ${response.status}`, data: null };
        }

        return { status: json?.status ?? true, msg: json?.msg, data: json?.data ?? null };
    } catch (error) {
        console.error("Failed to update profile:", error);
        return { status: false, msg: "Cập nhật thất bại", data: null };
    }
}
